import { useState } from 'react'
import { FaArrowRight as AssignIcon } from 'react-icons/fa'
import type { SampleSlice } from '@/hooks/useSampleUpload'
import type { BankId } from '@busswerks/shared'

const BANK_IDS: readonly BankId[] = ['A', 'B', 'C', 'D']

/**
 * MPC-style pad grid for triggering slices. Pads are 64px+ on a
 * side so a fingertip lands cleanly on both desktop pointer and
 * iPad touch.
 *
 * Layout: 4 columns. Wraps to as many rows as needed. 16 slices ⇒
 * a familiar 4×4 grid; 8 slices ⇒ 4×2; 32 slices ⇒ 4×8.
 *
 * Edit mode is off by default — the × delete affordance is hidden
 * in normal play mode so a wayward tap during performance can't
 * nuke a slice. The "Edit" toggle at the top right surfaces the ×
 * on every pad; tap again to return to play. The tiny bit of
 * friction (one extra tap to enter edit mode) is much cheaper than
 * the cost of an accidental delete mid-loop.
 *
 * The press state pulses a brief accent flash so the user gets
 * visual feedback that the trigger fired even when the audio's
 * tail is short or quiet.
 */

interface SlicePadGridProps {
  slices: SampleSlice[]
  disabled?: boolean
  onTrigger: (index: number) => void
  onDelete: (index: number) => void
  /** Per-slice pad routing. Called with the slice index + target
   *  bank + 0-indexed pad + kind (sample | granular). Slicer
   *  resolves to the matching PadContent and writes via
   *  `setPadContentInBank`. When absent, the per-slice picker is
   *  hidden. */
  onAssign?: (
    sliceIndex: number,
    bankId: BankId,
    padIndex: number,
    kind: 'sample' | 'granular' | 'instrument',
  ) => void
}

export function SlicePadGrid({
  slices,
  disabled,
  onTrigger,
  onDelete,
  onAssign,
}: SlicePadGridProps) {
  const [editMode, setEditMode] = useState(false)
  // Per-slice picker state — null when closed. Holding the index
  // here (rather than one-boolean-per-pad) keeps only one picker
  // open at a time.
  const [pickerSliceIdx, setPickerSliceIdx] = useState<number | null>(null)
  return (
    // Match the main sequencer's PadGrid frame: `rounded-xl`,
    // `bg-surface-sunken`, `border-brd-subtle`. 4-col gap-3
    // mirrors the main grid so pad widths read as consistent
    // across tools at the same column width.
    <section className="rounded-xl bg-surface-sunken border border-brd-subtle p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h3 className="text-[10px] uppercase tracking-wider text-content-tertiary">
          Slices ({slices.length})
        </h3>
        <button
          type="button"
          onClick={() => setEditMode(v => !v)}
          aria-pressed={editMode}
          title={
            editMode
              ? 'Done editing — hide delete controls'
              : 'Reveal delete (×) controls on every pad'
          }
          className={`text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-md border transition-colors ${
            editMode
              ? 'bg-destructive/20 text-destructive border-destructive/60'
              : 'bg-surface-base text-content-tertiary border-brd-subtle hover:text-content-secondary'
          }`}
        >
          {editMode ? '✓ Done' : 'Edit'}
        </button>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {slices.map((sl, i) => (
          <SlicePad
            key={sl.id}
            index={i}
            slice={sl}
            disabled={disabled}
            editMode={editMode}
            onTrigger={() => onTrigger(i)}
            onDelete={() => onDelete(i)}
            canAssign={Boolean(onAssign)}
            pickerOpen={pickerSliceIdx === i}
            onTogglePicker={() =>
              setPickerSliceIdx(pickerSliceIdx === i ? null : i)
            }
            onAssign={(bankId, padIndex, kind) => {
              onAssign?.(i, bankId, padIndex, kind)
              setPickerSliceIdx(null)
            }}
          />
        ))}
      </div>
    </section>
  )
}

interface SlicePadProps {
  index: number
  slice: SampleSlice
  disabled?: boolean
  editMode: boolean
  onTrigger: () => void
  onDelete: () => void
  canAssign: boolean
  pickerOpen: boolean
  onTogglePicker: () => void
  onAssign: (
    bankId: BankId,
    padIndex: number,
    kind: 'sample' | 'granular' | 'instrument',
  ) => void
}

function SlicePad({
  index,
  slice,
  disabled,
  editMode,
  onTrigger,
  onDelete,
  canAssign,
  pickerOpen,
  onTogglePicker,
  onAssign,
}: SlicePadProps) {
  const [pressed, setPressed] = useState(false)
  const len = Math.max(0, slice.endSec - slice.startSec)

  // 80ms flash. Long enough to register, short enough to keep up
  // with rapid finger-drumming.
  const flash = () => {
    setPressed(true)
    window.setTimeout(() => setPressed(false), 80)
  }

  const handleTrigger = () => {
    if (disabled) return
    // In edit mode, tapping the pad body is a no-op — the × is the
    // only interactive affordance. Prevents users from triggering
    // audio while cleaning up their chop list.
    if (editMode) return
    flash()
    onTrigger()
  }

  return (
    <div className="relative">
      <button
        type="button"
        onPointerDown={handleTrigger}
        disabled={disabled}
        title={`Slice ${index + 1} — ${slice.startSec.toFixed(3)}s → ${slice.endSec.toFixed(3)}s (${len.toFixed(3)}s)`}
        className={`w-full aspect-square rounded-xl border p-3 font-mono text-content-primary disabled:opacity-40 select-none transition-all flex flex-col items-center justify-center gap-0.5 ${
          pressed
            ? 'bg-gradient-to-br from-accent to-[color:var(--accent-dark)] border-accent text-[color:var(--accent-ink)] shadow-[0_0_20px_var(--accent-halo),inset_0_1px_0_rgba(255,255,255,0.2)]'
            : editMode
              ? 'bg-gradient-to-br from-surface-raised to-surface-base border-destructive/30 cursor-default'
              : 'bg-gradient-to-br from-surface-raised to-surface-base border-brd-subtle hover:border-[color:var(--accent-halo)] hover:-translate-y-0.5 active:bg-surface-raised'
        }`}
      >
        <span className="text-lg sm:text-xl tabular-nums leading-none">
          {index + 1}
        </span>
        <span className="text-[9px] text-content-tertiary tabular-nums leading-none">
          {len < 1 ? `${(len * 1000).toFixed(0)}ms` : `${len.toFixed(2)}s`}
        </span>
      </button>
      {editMode && (
        <button
          type="button"
          onPointerDown={e => {
            // Stop propagation so the parent pad doesn't also fire
            // (otherwise tapping × would trigger the slice + delete it
            // in the same pointer event).
            e.stopPropagation()
            if (!disabled) onDelete()
          }}
          disabled={disabled}
          aria-label={`Delete slice ${index + 1}`}
          title={`Delete slice ${index + 1}`}
          className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-destructive text-[color:var(--destructive-ink)] border-2 border-surface-sunken shadow-md text-[12px] font-mono leading-none flex items-center justify-center disabled:opacity-40 hover:scale-110 transition-transform"
        >
          ×
        </button>
      )}
      {/* Per-slice assign affordance. Visible in Edit mode only so
          the primary play gesture stays untouched when users are
          performing. Opens a compact bank + pad picker anchored
          below the slice pad. */}
      {editMode && canAssign && (
        <button
          type="button"
          onPointerDown={e => {
            e.stopPropagation()
            if (!disabled) onTogglePicker()
          }}
          disabled={disabled}
          aria-label={`Assign slice ${index + 1} to a pad`}
          aria-expanded={pickerOpen}
          title={`Route slice ${index + 1} to a specific Bank + Pad`}
          className={`absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full border-2 border-surface-sunken shadow-md text-[10px] leading-none flex items-center justify-center disabled:opacity-40 hover:scale-110 transition-transform ${
            pickerOpen
              ? 'bg-accent text-[color:var(--accent-ink)]'
              : 'bg-surface-base text-content-secondary'
          }`}
        >
          <AssignIcon size={9} />
        </button>
      )}
      {pickerOpen && (
        <SliceAssignPicker
          sliceIndex={index}
          onPick={(bankId, padIndex, kind) => onAssign(bankId, padIndex, kind)}
          onClose={onTogglePicker}
        />
      )}
    </div>
  )
}

/** Compact bank+pad picker. 4 bank tabs + a 4×4 pad grid;
 *  clicking a pad assigns the slice and closes the popover.
 *  Positioned absolutely below the slice pad via a portal-like
 *  overlay inside the same relative parent. */
const SliceAssignPicker: React.FC<{
  sliceIndex: number
  onPick: (
    bankId: BankId,
    padIndex: number,
    kind: 'sample' | 'granular' | 'instrument',
  ) => void
  onClose: () => void
}> = ({ sliceIndex, onPick, onClose }) => {
  const [bank, setBank] = useState<BankId>('A')
  // Assign-kind picker — defaults to 'sample' (the path users
  // already know). 'granular' seeds a Freeze-style pad on the
  // slice; 'instrument' writes a sampler-instrument pad (Wave 3)
  // so triggers flow through triggerInstrument + the buffer
  // injection path.
  const [assignKind, setAssignKind] = useState<
    'sample' | 'granular' | 'instrument'
  >('sample')
  return (
    <div
      role="dialog"
      aria-label={`Assign slice ${sliceIndex + 1}`}
      className="absolute top-full left-0 right-0 mt-2 z-30 rounded-lg bg-surface-raised border border-brd-subtle shadow-lg p-2 min-w-[180px]"
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Slice {sliceIndex + 1} → bank
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-content-tertiary hover:text-content-primary text-[10px] leading-none"
        >
          ×
        </button>
      </div>
      <div className="flex gap-1 mb-2">
        {BANK_IDS.map(b => (
          <button
            key={b}
            type="button"
            onClick={() => setBank(b)}
            aria-pressed={bank === b}
            className={`flex-1 py-1 rounded text-[11px] font-mono transition-colors ${
              bank === b
                ? 'bg-accent text-[color:var(--accent-ink)]'
                : 'bg-surface-sunken text-content-secondary hover:text-content-primary'
            }`}
          >
            {b}
          </button>
        ))}
      </div>
      <div
        role="radiogroup"
        aria-label="Assign kind"
        className="flex gap-1 mb-2"
      >
        {(
          [
            { key: 'sample', label: 'Sample', hint: 'Legacy sample pad' },
            {
              key: 'granular',
              label: 'Granular',
              hint: 'Freeze-style granular pad on the slice boundary',
            },
            {
              key: 'instrument',
              label: 'Sampler',
              hint: 'Sampler instrument pad — triggers through the plugin host',
            },
          ] as const
        ).map(k => (
          <button
            key={k.key}
            type="button"
            role="radio"
            aria-checked={assignKind === k.key}
            onClick={() => setAssignKind(k.key)}
            title={k.hint}
            className={`flex-1 py-1 rounded text-[9px] font-mono uppercase tracking-[0.14em] transition-colors ${
              assignKind === k.key
                ? 'bg-accent text-[color:var(--accent-ink)]'
                : 'bg-surface-sunken text-content-secondary hover:text-content-primary'
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-1">
        {Array.from({ length: 16 }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(bank, i, assignKind)}
            title={`Assign to Bank ${bank} · Pad ${i + 1} (${assignKind})`}
            className="aspect-square rounded bg-surface-sunken text-[10px] font-mono text-content-secondary hover:bg-accent/30 hover:text-accent transition-colors tabular-nums"
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  )
}
